/**
 * Utility for mapping API error responses to user-friendly messages.
 *
 * This module provides functions to translate technical API errors
 * into clear, actionable messages for end users.
 */

import { ERROR_MESSAGES } from '../constants/errorMessages';

/**
 * Standard API error response structure with field-specific errors
 * and a special __all__ key for non-field errors.
 */
export interface ApiErrorBody {
  __all__?: string[];
  [field: string]: string[] | undefined;
}

/**
 * Known API error keys that we can map to specific messages.
 * Add new error patterns here as the API evolves.
 */
const KNOWN_ERROR_PATTERNS = {
  LOUNGE_ACCESS: {
    // Pattern matching for __all__ errors
    NO_SESSION: /no session found/i,
  },
} as const;

/**
 * Maps a lounge access API error to a user-friendly message.
 *
 * @param errorBody - The error body from the API response
 * @returns A user-friendly error message
 */
export function getLoungeAccessErrorMessage(
  errorBody: ApiErrorBody | undefined | null,
): string {
  if (!errorBody) {
    return ERROR_MESSAGES.GENERIC;
  }

  // Check for __all__ (non-field) errors first
  if (errorBody.__all__?.length) {
    const allError = errorBody.__all__[0];

    // Match against known error patterns
    if (KNOWN_ERROR_PATTERNS.LOUNGE_ACCESS.NO_SESSION.test(allError)) {
      return ERROR_MESSAGES.LOUNGE_ACCESS.NO_SESSION_FOUND;
    }

    // Unknown __all__ error - return generic but log for debugging
    console.warn('Unknown lounge access error:', allError);
    return ERROR_MESSAGES.GENERIC;
  }

  // No __all__ error found
  return ERROR_MESSAGES.GENERIC;
}

/**
 * Generic error message mapper that can be extended for different API endpoints.
 *
 * @param endpoint - The API endpoint identifier
 * @param errorBody - The error body from the API response
 * @returns A user-friendly error message
 */
export function getApiErrorMessage(
  endpoint: 'lounge_access' | 'survey' | 'auth',
  errorBody: ApiErrorBody | undefined | null,
): string {
  switch (endpoint) {
    case 'lounge_access':
      return getLoungeAccessErrorMessage(errorBody);
    // Add more endpoints as needed
    case 'survey':
    case 'auth':
    default:
      return ERROR_MESSAGES.GENERIC;
  }
}
